<template>
  <div class="card rounded p-3">
    <h2 class="text-xl mt-0">
      <i
        class="mr-2 text-primary-700 dark:text-primary-300"
        :class="[ icon ]"
      /> {{ title }}
    </h2>

    <input
      ref="fileInput"
      type="file"
      class="hidden"
      @change="importEvent"
    >
    <div class="w-full flex space-x-2 2xl:space-x-4">
      <al-button
        v-if="!importDisabled"
        :loading="loading"
        full
        @click="$refs.fileInput.click()"
      >
        <i class="w-4 h-4 mr-2 fas fa-download" /> Import
      </al-button>

      <al-button
        full
        :loading="loading"
        @click="dateRangeHandler"
      >
        <i class="w-4 h-4 mr-2 fas fa-upload" /> Export
      </al-button>
    </div>

    <slot />

    <al-dialog
      v-model="dialog"
      :title="`Export ${title}`"
    >
      <div class="flex space-x-2">
        <div class="w-1/4">
          <!-- <al-button
            :primary="rangeActive === CalendarNavigator.AllTime"
            full
            small
            @click="allTime"
          >
            All Time
          </al-button> -->
          <al-button
            :primary="rangeActive === CalendarNavigator.Today"
            full
            small
            @click="today"
          >
            Today
          </al-button>
          <al-button
            :primary="rangeActive === CalendarNavigator.Yesterday"
            full
            small
            @click="yesterday"
          >
            Yesterday
          </al-button>
          <al-button
            :primary="rangeActive === CalendarNavigator.Last7Days"
            full
            small
            @click="last7days"
          >
            Last 7 Days
          </al-button>
          <al-button
            :primary="rangeActive === CalendarNavigator.LastWeek"
            full
            small
            @click="lastWeek"
          >
            Last Week
          </al-button>
          <al-button
            :primary="rangeActive === CalendarNavigator.Last2Weeks"
            full
            small
            @click="last2Weeks"
          >
            Last 2 Weeks
          </al-button>
          <al-button
            :primary="rangeActive === CalendarNavigator.ThisMonth"
            full
            small
            @click="thisMonth"
          >
            This Month
          </al-button>
          <al-button
            :primary="rangeActive === CalendarNavigator.LastMonth"
            full
            small
            @click="lastMonth"
          >
            Last Month
          </al-button>
        </div>

        <div
          class="w-3/4"
          :class="{
            dark: isDark
          }"
        >
          <vc-date-picker
            ref="datePicker"
            is-expanded
            is-range
            title-position="right"
            :masks="{
              input: ['YYYY-MM-DD']
            }"
            :value="date"
            :max-date="new Date()"
            :is-dark="isDark"
            @dayclick="rangeActive = null"
            @input="chooseManual"
          />
        </div>
      </div>

      <template #actions="{ close }">
        <al-button
          outlined
          @click="close()"
        >
          Cancel
        </al-button>
        <al-button
          primary
          @click="exportEvent"
        >
          <i class="w-4 h-4 mr-2 fas fa-upload" /> Export
        </al-button>
      </template>
    </al-dialog>
  </div>
</template>

<script>
import { formattedDate } from '@/utils/dateUtil';

const CalendarNavigator = {
  Today: 1,
  Yesterday: 2,
  Last7Days: 3,
  LastWeek: 4,
  Last2Weeks: 5,
  ThisMonth: 6,
  LastMonth: 7,
  AllTime: 8
};

export default {
  name: 'ReportCardItem',
  components: {
    'al-dialog': () => import('@/components/core/Dialog'),
    'al-button': () => import('@/components/core/Button')
  },
  props: {
    title: {
      type: String,
      default: ''
    },
    icon: {
      type: String,
      default: ''
    },
    importDisabled: {
      type: Boolean,
      default: false
    },
    isDateRange: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    dialog: false,
    date: {
      start: new Date(),
      end: new Date()
    },
    rangeActive: CalendarNavigator.Today,
    CalendarNavigator
  }),
  computed: {
    isDark() {
      return this.$store.getters['common/getDarkMode'];
    }
  },
  methods: {
    chooseManual(e) {
      this.date = e;
    },
    importEvent() {
      const file = this.$refs.fileInput.files[0];

      if (file) {
        this.$emit('import', file);

        this.$refs.fileInput.files = null;
      }
    },
    dateRangeHandler() {
      if (this.isDateRange) {
        this.dialog = true;
      } else {
        this.$emit('export');
      }
    },
    exportEvent() {
      this.dialog = false;
      this.$emit('export', {
        start: formattedDate(this.date.start),
        end: formattedDate(this.date.end)
      });
    },
    allTime() {
      this.date = {
        start: null,
        end: null
      };

      this.rangeActive = CalendarNavigator.AllTime;
      this.$refs.datePicker.move(new Date());
    },
    lastMonth() {
      const today = new Date();

      this.date = {
        start: new Date(today.getFullYear(), today.getMonth() - 1, 1),
        end: new Date(today.getFullYear(), today.getMonth(), 0)
      };

      this.rangeActive = CalendarNavigator.LastMonth;
      this.$refs.datePicker.move(this.date.start);
    },
    thisMonth() {
      const today = new Date();

      this.date = {
        start: new Date(today.getFullYear(), today.getMonth(), 1),
        end: today
      };

      this.rangeActive = CalendarNavigator.ThisMonth;
      this.$refs.datePicker.move(this.date.start);
    },
    last2Weeks() {
      const today = new Date();

      this.date = {
        start: new Date(today.getFullYear(), today.getMonth(), today.getDate() - 14),
        end: today
      };

      this.rangeActive = CalendarNavigator.Last2Weeks;
      this.$refs.datePicker.move(this.date.start);
    },
    last7days() {
      const today = new Date();

      this.date = {
        start: new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7),
        end: today
      };

      this.rangeActive = CalendarNavigator.Last7Days;
      this.$refs.datePicker.move(this.date.start);
    },
    lastWeek() {
      const today = new Date();
      const startDateOfPrevWeek = new Date(today.getFullYear(), today.getMonth(), today.getDate() - (6 + today.getDay()));

      this.date = {
        start: startDateOfPrevWeek,
        end: new Date(startDateOfPrevWeek.getFullYear(), startDateOfPrevWeek.getMonth(), startDateOfPrevWeek.getDate() + 6)
      };

      this.rangeActive = CalendarNavigator.LastWeek;
      this.$refs.datePicker.move(this.date.start);
    },
    yesterday() {
      const today = new Date();
      const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);

      this.date = {
        start: yesterday,
        end: yesterday
      };

      this.rangeActive = CalendarNavigator.Yesterday;
      this.$refs.datePicker.move(this.date.start);
    },
    today() {
      const today = new Date();

      this.date = {
        start: today,
        end: today
      };

      this.rangeActive = CalendarNavigator.Today;
      this.$refs.datePicker.move(this.date.start);
    }
  }
};
</script>

<style lang="scss" scoped>
:deep {
  .vc-container {
    @apply
      text-muted-950
      dark:text-muted-200
      border-muted-300
      bg-muted-100
      dark:border-muted-600
      dark:bg-muted-700
      rounded
      text-sm
  }
}
</style>
